import { supabase } from '@/lib/supabase';

export type QuestionSetStatus = 'draft' | 'published' | 'archived';
export type CorrectOption = 'A' | 'B' | 'C' | 'D';

export type TeacherQuestionSetListItem = {
  class_id: string;
  class_label: string;
  created_at: string;
  description: string | null;
  id: string;
  question_count: number;
  school_id: string;
  status: QuestionSetStatus;
  subject_id: string;
  subject_label: string;
  teacher_id: string;
  teacher_name: string | null;
  title: string;
  updated_at: string;
};

export type QuestionListItem = {
  correct_option: CorrectOption;
  explanation: string | null;
  id: string;
  marks: number;
  option_a: string;
  option_b: string;
  option_c: string;
  option_d: string;
  position: number;
  prompt: string;
  question_set_id: string;
  school_id: string;
};

const TEACHER_QUESTION_SETS_FUNCTION = 'teacher-question-sets';

function getFunctionErrorStatus(error: unknown) {
  const context = (error as { context?: { status?: unknown } } | null)?.context;
  return typeof context?.status === 'number' ? context.status : null;
}

export async function fetchTeacherQuestionSets(teacherId: string) {
  try {
    const { data, error } = await supabase.functions.invoke(TEACHER_QUESTION_SETS_FUNCTION, {
      body: {
        action: 'list',
      },
    });

    if (error) {
      throw error;
    }

    return ((data?.sets as TeacherQuestionSetListItem[] | undefined) ?? []).map((set) => ({
      ...set,
      teacher_name: set.teacher_name ?? null,
    }));
  } catch (functionError) {
    if (getFunctionErrorStatus(functionError) !== null) {
      throw functionError;
    }
    console.warn('Teacher question-set function unavailable, falling back to direct query:', functionError);
  }

  return fetchTeacherQuestionSetsDirect(teacherId);
}

async function fetchTeacherQuestionSetsDirect(teacherId: string) {
  const { data: assignments, error: assignmentsError } = await supabase
    .from('class_subjects')
    .select('class_id, school_id, subject_id')
    .eq('teacher_id', teacherId);

  if (assignmentsError) throw assignmentsError;

  const assignmentRows = assignments ?? [];
  if (assignmentRows.length === 0) {
    return [] as TeacherQuestionSetListItem[];
  }

  const assignedKeys = new Set(
    assignmentRows.map((assignment) =>
      buildAssignmentKey(assignment.school_id, assignment.class_id, assignment.subject_id)
    )
  );
  const classIds = [...new Set(assignmentRows.map((assignment) => assignment.class_id))];
  const schoolIds = [...new Set(assignmentRows.map((assignment) => assignment.school_id))];
  const subjectIds = [...new Set(assignmentRows.map((assignment) => assignment.subject_id))];

  const { data: sets, error: setsError } = await supabase
    .from('question_sets')
    .select(`
      id,
      title,
      description,
      class_id,
      subject_id,
      teacher_id,
      school_id,
      status,
      created_at,
      updated_at,
      classes (name, section),
      subjects (name, code)
    `)
    .in('class_id', classIds)
    .in('school_id', schoolIds)
    .in('subject_id', subjectIds)
    .order('updated_at', { ascending: false });

  if (setsError) throw setsError;

  const visibleSets = ((sets ?? []) as any[]).filter((set) =>
    assignedKeys.has(buildAssignmentKey(set.school_id, set.class_id, set.subject_id))
  );

  const setIds = visibleSets.map((set) => set.id);
  let questionCounts = new Map<string, number>();

  if (setIds.length > 0) {
    const { data: questions, error: questionsError } = await supabase
      .from('questions')
      .select('question_set_id')
      .in('question_set_id', setIds);

    if (questionsError) throw questionsError;

    for (const question of questions ?? []) {
      questionCounts.set(
        question.question_set_id,
        (questionCounts.get(question.question_set_id) ?? 0) + 1
      );
    }
  }

  const teacherIds = [...new Set(visibleSets.map((set) => set.teacher_id).filter(Boolean))];
  const teacherNames = new Map<string, string | null>();

  if (teacherIds.length > 0) {
    const { data: teachers, error: teachersError } = await supabase
      .from('profiles')
      .select('id, full_name')
      .in('id', teacherIds);

    if (teachersError) throw teachersError;

    for (const teacher of teachers ?? []) {
      teacherNames.set(teacher.id, teacher.full_name ?? null);
    }
  }

  return visibleSets.map((set) => ({
    id: set.id,
    title: set.title,
    description: set.description,
    class_id: set.class_id,
    subject_id: set.subject_id,
    teacher_id: set.teacher_id,
    school_id: set.school_id,
    status: set.status,
    created_at: set.created_at,
    updated_at: set.updated_at,
    class_label: set.classes?.section?.trim()
      ? `${set.classes.name} - ${set.classes.section}`
      : set.classes?.name ?? 'Class unavailable',
    teacher_name: teacherNames.get(set.teacher_id) ?? null,
    subject_label: set.subjects?.code?.trim()
      ? `${set.subjects.name} (${set.subjects.code})`
      : set.subjects?.name ?? 'Subject unavailable',
    question_count: questionCounts.get(set.id) ?? 0,
  })) as TeacherQuestionSetListItem[];
}

function buildAssignmentKey(schoolId: string, classId: string, subjectId: string) {
  return `${schoolId}:${classId}:${subjectId}`;
}

export async function fetchQuestionsForSet(questionSetId: string) {
  try {
    const { data, error } = await supabase.functions.invoke(TEACHER_QUESTION_SETS_FUNCTION, {
      body: {
        action: 'questions',
        questionSetId,
      },
    });

    if (error) {
      throw error;
    }

    return (data?.questions ?? []) as QuestionListItem[];
  } catch (functionError) {
    if (getFunctionErrorStatus(functionError) !== null) {
      throw functionError;
    }
    console.warn('Teacher question fetch function unavailable, falling back to direct query:', functionError);
  }

  const { data, error } = await supabase
    .from('questions')
    .select(`
      id,
      question_set_id,
      prompt,
      option_a,
      option_b,
      option_c,
      option_d,
      correct_option,
      explanation,
      marks,
      position,
      school_id
    `)
    .eq('question_set_id', questionSetId)
    .order('position', { ascending: true });

  if (error) throw error;

  return (data ?? []) as QuestionListItem[];
}

export async function createQuestionSet(input: {
  classId: string;
  description?: string;
  schoolId: string;
  subjectId: string;
  teacherId: string;
  title: string;
}) {
  const { data, error } = await supabase
    .from('question_sets')
    .insert({
      class_id: input.classId,
      description: input.description?.trim() || null,
      school_id: input.schoolId,
      subject_id: input.subjectId,
      teacher_id: input.teacherId,
      title: input.title.trim(),
    })
    .select('id')
    .single();

  if (error) throw error;

  return data.id as string;
}

export async function updateQuestionSetStatus(input: {
  questionSetId: string;
  schoolId: string;
  status: QuestionSetStatus;
}) {
  try {
    const { data, error } = await supabase.functions.invoke(TEACHER_QUESTION_SETS_FUNCTION, {
      body: {
        action: 'update_status',
        questionSetId: input.questionSetId,
        status: input.status,
      },
    });

    if (error) {
      throw error;
    }

    if (data?.questionSet?.id) {
      return data.questionSet as { id: string; status: QuestionSetStatus };
    }
  } catch (functionError) {
    if (getFunctionErrorStatus(functionError) !== null) {
      throw functionError;
    }
    console.warn('Teacher question-set status function unavailable, falling back to direct update:', functionError);
  }

  const { data, error } = await supabase
    .from('question_sets')
    .update({
      status: input.status,
    })
    .eq('id', input.questionSetId)
    .eq('school_id', input.schoolId)
    .select('id, status')
    .single();

  if (error) throw error;

  return data as { id: string; status: QuestionSetStatus };
}

export async function createQuestion(input: {
  correctOption: CorrectOption;
  explanation?: string;
  marks?: number;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  position: number;
  prompt: string;
  questionSetId: string;
  schoolId: string;
}) {
  const { error } = await supabase.from('questions').insert({
    correct_option: input.correctOption,
    explanation: input.explanation?.trim() || null,
    marks: input.marks ?? 1,
    option_a: input.optionA.trim(),
    option_b: input.optionB.trim(),
    option_c: input.optionC.trim(),
    option_d: input.optionD.trim(),
    position: input.position,
    prompt: input.prompt.trim(),
    question_set_id: input.questionSetId,
    school_id: input.schoolId,
  });

  if (error) throw error;
}

export type DraftQuestion = {
  id: string;
  questionId?: string;
  prompt: string;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  correctOption: CorrectOption;
  explanation: string;
  marks: number;
};

export type McqGenerationDifficulty = 'easy' | 'medium' | 'hard' | 'mixed';

type GeneratedMcqDraftPayload = {
  correctOption: CorrectOption;
  explanation: string;
  marks: number;
  optionA: string;
  optionB: string;
  optionC: string;
  optionD: string;
  prompt: string;
};

export function createDraftFromQuestion(question: QuestionListItem): DraftQuestion {
  return {
    id: `existing_${question.id}`,
    questionId: question.id,
    prompt: question.prompt,
    optionA: question.option_a,
    optionB: question.option_b,
    optionC: question.option_c,
    optionD: question.option_d,
    correctOption: question.correct_option,
    explanation: question.explanation ?? '',
    marks: question.marks,
  };
}

export async function createQuestionsBatch(
  questions: DraftQuestion[],
  questionSetId: string,
  schoolId: string,
  startPosition: number,
) {
  const rows = questions.map((q, index) => ({
    correct_option: q.correctOption,
    explanation: q.explanation?.trim() || null,
    marks: q.marks,
    option_a: q.optionA.trim(),
    option_b: q.optionB.trim(),
    option_c: q.optionC.trim(),
    option_d: q.optionD.trim(),
    position: startPosition + index,
    prompt: q.prompt.trim(),
    question_set_id: questionSetId,
    school_id: schoolId,
  }));

  const { error } = await supabase.from('questions').insert(rows);

  if (error) throw error;
}

export async function syncQuestionsBatch(
  questions: DraftQuestion[],
  questionSetId: string,
  schoolId: string
) {
  try {
    const { error } = await supabase.functions.invoke(TEACHER_QUESTION_SETS_FUNCTION, {
      body: {
        action: 'sync_questions',
        questionSetId,
        questions,
      },
    });

    if (error) {
      throw error;
    }

    return;
  } catch (functionError) {
    if (getFunctionErrorStatus(functionError) !== null) {
      throw functionError;
    }
    console.warn('Teacher question sync function unavailable, falling back to direct sync:', functionError);
  }

  const existingRows = questions
    .filter((question) => question.questionId)
    .map((question, index) => ({
      id: question.questionId as string,
      correct_option: question.correctOption,
      explanation: question.explanation?.trim() || null,
      marks: question.marks,
      option_a: question.optionA.trim(),
      option_b: question.optionB.trim(),
      option_c: question.optionC.trim(),
      option_d: question.optionD.trim(),
      position: index + 1,
      prompt: question.prompt.trim(),
      question_set_id: questionSetId,
      school_id: schoolId,
    }));

  if (existingRows.length > 0) {
    const { error } = await supabase.from('questions').upsert(existingRows, { onConflict: 'id' });

    if (error) throw error;
  }

  const newRows = questions
    .filter((question) => !question.questionId)
    .map((question, index) => ({
      correct_option: question.correctOption,
      explanation: question.explanation?.trim() || null,
      marks: question.marks,
      option_a: question.optionA.trim(),
      option_b: question.optionB.trim(),
      option_c: question.optionC.trim(),
      option_d: question.optionD.trim(),
      position: existingRows.length + index + 1,
      prompt: question.prompt.trim(),
      question_set_id: questionSetId,
      school_id: schoolId,
    }));

  if (newRows.length > 0) {
    const { error } = await supabase.from('questions').insert(newRows);

    if (error) throw error;
  }
}

export async function generateMcqDrafts(input: {
  count: number;
  difficulty: McqGenerationDifficulty;
  instructions?: string;
  questionSetId: string;
  topic?: string;
}) {
  const { data, error } = await supabase.functions.invoke('teacher-generate-mcqs', {
    body: {
      count: input.count,
      difficulty: input.difficulty,
      instructions: input.instructions?.trim() || null,
      question_set_id: input.questionSetId,
      topic: input.topic?.trim() || null,
    },
  });

  if (error) {
    const errorMessage = (error as { data?: { message?: string } })?.data?.message || error.message;
    throw new Error(errorMessage || 'Unable to generate MCQs right now.');
  }

  const questions = Array.isArray(data?.questions) ? (data.questions as GeneratedMcqDraftPayload[]) : null;

  if (!questions || questions.length === 0) {
    throw new Error('The AI response did not return any MCQs.');
  }

  return questions.map((question, index) => ({
    id: `ai_${Date.now()}_${index}_${Math.random().toString(36).slice(2, 8)}`,
    prompt: question.prompt,
    optionA: question.optionA,
    optionB: question.optionB,
    optionC: question.optionC,
    optionD: question.optionD,
    correctOption: question.correctOption,
    explanation: question.explanation ?? '',
    marks: question.marks ?? 1,
  })) as DraftQuestion[];
}
